import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    updateProfile,
    GoogleAuthProvider,
    signInWithPopup,
    getRedirectResult,
    sendPasswordResetEmail
} from 'firebase/auth';
import type { User, UserCredential } from 'firebase/auth';
import { auth, db } from '../lib/firebase';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

interface AuthContextType {
    currentUser: User | null;
    loading: boolean;
    login: (email: string, pass: string) => Promise<UserCredential>;
    register: (email: string, pass: string, username: string) => Promise<UserCredential>;
    loginWithGoogle: () => Promise<UserCredential>;
    resetPassword: (email: string) => Promise<void>;
    checkUserExists: (uid: string) => Promise<boolean>;
    completeUserSetup: (user: User, username: string) => Promise<void>;
    logout: () => Promise<void>;
    updateUserAvatar: (avatarUrl: string) => Promise<void>;
    updateUsername: (newName: string) => Promise<void>;
    getGoogleRedirectResult: () => Promise<UserCredential | null>;
    isAdmin: boolean;
}

export const ADMIN_EMAIL = import.meta.env.VITE_ADMIN_EMAIL || 'aadish14146yadav@gmail.com';

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const checkUserExists = async (uid: string) => {
        const docRef = doc(db, 'users', uid);
        const docSnap = await getDoc(docRef);
        return docSnap.exists();
    };

    const completeUserSetup = async (user: User, username: string) => {
        const styles = ['thumbs', 'bottts-neutral', 'fun-emoji'];
        const randomStyle = styles[Math.floor(Math.random() * styles.length)];
        const avatarUrl = `https://api.dicebear.com/7.x/${randomStyle}/svg?seed=${username}`;

        await updateProfile(user, {
            displayName: username,
            photoURL: avatarUrl
        });

        await setDoc(doc(db, 'users', user.uid), {
            uid: user.uid,
            email: user.email,
            displayName: username,
            photoURL: avatarUrl,
            createdAt: serverTimestamp(),
            preferences: { theme: 'dark' }
        });

        setCurrentUser({ ...user, displayName: username, photoURL: avatarUrl } as User);
    };

    const loginWithGoogle = async (): Promise<UserCredential> => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
            prompt: 'select_account' 
        });
        return signInWithPopup(auth, provider);
    };

    const getGoogleRedirectResult = () => {
        return getRedirectResult(auth);
    };

    const resetPassword = (email: string) => {
        return sendPasswordResetEmail(auth, email);
    };

    const login = (email: string, pass: string) => {
        return signInWithEmailAndPassword(auth, email, pass);
    };

    const register = async (email: string, pass: string, username: string) => {
        const userCredential = await createUserWithEmailAndPassword(auth, email, pass);
        await completeUserSetup(userCredential.user, username);
        return userCredential;
    };

    const logout = () => {
        return signOut(auth);
    };

    const updateUserAvatar = async (avatarUrl: string) => {
        if (!currentUser) return;
        await updateProfile(currentUser, { photoURL: avatarUrl });
        const docRef = doc(db, 'users', currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
             await updateDoc(docRef, { photoURL: avatarUrl });
        } else {
             await setDoc(docRef, { photoURL: avatarUrl }, { merge: true });
        }
        setCurrentUser({ ...currentUser, photoURL: avatarUrl } as User);
    };

    const updateUsername = async (newName: string) => {
        if (!currentUser) return;
        await updateProfile(currentUser, { displayName: newName });
        const docRef = doc(db, 'users', currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
             await updateDoc(docRef, { displayName: newName, username: newName });
        } else {
             await setDoc(docRef, { displayName: newName, username: newName }, { merge: true });
        }
        setCurrentUser({ ...currentUser, displayName: newName } as User);
    };

    const isAdmin = currentUser?.email === ADMIN_EMAIL;

    const value = {
        currentUser,
        loading,
        login,
        register,
        loginWithGoogle,
        resetPassword,
        checkUserExists,
        completeUserSetup,
        logout,
        updateUserAvatar,
        updateUsername,
        getGoogleRedirectResult,
        isAdmin
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}